import { useState, useRef, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { SIZE_DATA, CERT_OPTIONS, isRingProduct } from "../data/options";
import styles from "./ProductDetail.module.css";

export default function ProductDetail({ product, inModal = false, onClose }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [added, setAdded] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [sizeSystem, setSizeSystem] = useState("us");
  const [sizeError, setSizeError] = useState(false);
  const [certificate, setCertificate] = useState("");
  const [certOpen, setCertOpen] = useState(false);
  const [certError, setCertError] = useState(false);
  const certRef = useRef(null);
  const { addToCart, items } = useCart();

  const selectedCert = CERT_OPTIONS.find((c) => c.value === certificate) || null;
  const certFee = selectedCert ? selectedCert.price : 0;
  const totalPrice = product.price + certFee;

  useEffect(() => {
    const handleClick = (e) => {
      if (certRef.current && !certRef.current.contains(e.target)) setCertOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isRing = isRingProduct(product);
  const inCart = items.some((i) => i.id === product.id);
  const installment = Math.ceil(totalPrice / 3).toLocaleString();
  const isImage = (src) => /\.(png|jpg|jpeg|webp)/i.test(src);

  const handlePlay = () => {
    if (playing) { videoRef.current.pause(); setPlaying(false); }
    else { videoRef.current.play(); setPlaying(true); }
  };

  const handleAddToCart = () => {
    if (isRing && !selectedSize) {
      setSizeError(true);
      setTimeout(() => setSizeError(false), 2000);
      return;
    }
    if (!certificate) {
      setCertError(true);
      setTimeout(() => setCertError(false), 2000);
      return;
    }
    const sizeLabel = selectedSize
      ? `${sizeSystem === "us" ? "US" : "JP"} ${selectedSize}`
      : null;
    addToCart({
      ...product,
      price: totalPrice,
      basePrice: product.price,
      selectedSize: sizeLabel,
      certificate: selectedCert && certFee > 0 ? selectedCert.label : null,
      certificateFee: certFee,
    });
    setAdded(true);
    if (inModal) {
      setTimeout(() => onClose?.(), 700);
    } else {
      setTimeout(() => setAdded(false), 2000);
    }
  };

  const handleInquire = () => {
    const msg = encodeURIComponent(`Hello! I'm interested in the ${product.name}. Could you provide more details?`);
    const link = document.createElement("a");
    link.href = `https://wa.me/94765851400?text=${msg}`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  return (
    <div className={`${styles.page} ${inModal ? styles.pageInModal : ""}`}>

      <div className={styles.inner}>
        {!inModal && (
          <button className={styles.backBtn} onClick={() => window.history.back()}>← Back</button>
        )}

        <div className={styles.grid}>
          {/* Media */}
          <div className={styles.videoWrap} onClick={isImage(product.src) ? undefined : handlePlay}
            style={{ cursor: isImage(product.src) ? "default" : "pointer" }}>
            {isImage(product.src) ? (
              <img src={product.src} alt={product.name} className={styles.video} style={{ objectFit: "contain", background: "#f5ede4" }} />
            ) : (
              <video ref={videoRef} src={product.src} preload="metadata" loop muted playsInline
                onEnded={() => setPlaying(false)} className={styles.video} />
            )}
            <div className={styles.videoOverlay} />
            <span className={styles.gemBadge}>{product.gem}</span>
            {!isImage(product.src) && !playing && (
              <div className={styles.playBtn}><div className={styles.playArrow} /></div>
            )}
            {!isImage(product.src) && playing && (
              <div className={styles.pauseBtn}>
                <div className={styles.pauseBar} /><div className={styles.pauseBar} />
              </div>
            )}
          </div>

          {/* Info */}
          <div className={styles.info}>
            <p className={styles.eyebrow}>Ratnapura Gems</p>
            <h1 className={styles.name}>{product.name}</h1>

            <div className={styles.priceRow}>
              <span className={styles.price}>LKR {totalPrice.toLocaleString()}</span>
            </div>

            <div className={styles.mintpayLine}>
              <span className={styles.mintpayText}>or 3 × LKR {installment} with</span>
              <img src="/mintpay-logo.webp" alt="Mintpay" className={styles.mintpayLogo} />
            </div>

            <div className={styles.divider} />
            <p className={styles.description}>{product.description}</p>

            {/* Ring Size Selector */}
            {isRing && (
              <div className={styles.sizeSection}>
                <div className={styles.sizeHeader}>
                  <div className={styles.sizeHeaderLeft}>
                    <span className={styles.sizeLabel}>Select Size</span>
                    <div className={styles.sizeToggle}>
                      <button
                        className={`${styles.sizeToggleBtn} ${sizeSystem === "us" ? styles.sizeToggleActive : ""}`}
                        onClick={() => { setSizeSystem("us"); setSelectedSize(null); }}
                      >US</button>
                      <button
                        className={`${styles.sizeToggleBtn} ${sizeSystem === "jp" ? styles.sizeToggleActive : ""}`}
                        onClick={() => { setSizeSystem("jp"); setSelectedSize(null); }}
                      >JP / CN</button>
                    </div>
                  </div>
                  <button className={styles.sizeGuideBtn} onClick={() => window.location.href = "/size-guide"}>
                    📏 Size Guide
                  </button>
                </div>

                <div className={styles.sizeGrid}>
                  {SIZE_DATA.map(s => {
                    const val = sizeSystem === "us" ? s.us : String(s.jp);
                    return (
                      <button
                        key={s.mm}
                        className={`${styles.sizeBox} ${selectedSize === val ? styles.sizeBoxSelected : ""}`}
                        onClick={() => { setSelectedSize(val); setSizeError(false); }}
                      >
                        {val}
                      </button>
                    );
                  })}
                </div>
                {sizeError && (
                  <p className={styles.sizeError}>Please select a size before adding to cart</p>
                )}
              </div>
            )}

            {/* Gem Details */}
            <div className={styles.detailsGrid}>
              {Object.entries(product.details).map(([key, value]) => (
                <div key={key} className={`${styles.detailItem} ${key === "certification" ? styles.detailItemFull : ""}`}>
                  <span className={styles.detailKey}>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                  {key !== "certification" && (
                    <span className={styles.detailValue}>{value}</span>
                  )}
                  {key === "certification" && (
                    <div className={styles.certWrap} ref={certRef}>
                      <button
                        type="button"
                        className={`${styles.certTrigger} ${certError ? styles.certTriggerError : ""}`}
                        onClick={() => setCertOpen(!certOpen)}
                      >
                        <span className={selectedCert ? undefined : styles.certPlaceholder}>
                          {selectedCert ? selectedCert.label : "Select certificate type"}
                        </span>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>

                      {certOpen && (
                        <div className={styles.certPanel}>
                          {CERT_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className={styles.certRow}
                              onClick={() => { setCertificate(opt.value); setCertOpen(false); setCertError(false); }}
                            >
                              <span className={`${styles.certRadio} ${certificate === opt.value ? styles.certRadioActive : ""}`}>
                                {certificate === opt.value && <span className={styles.certRadioInner} />}
                              </span>
                              <span className={styles.certRowLabel}>{opt.label}</span>
                              {opt.price > 0 && (
                                <span className={styles.certRowPrice}>
                                  + {opt.price.toLocaleString()}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                      {certError && (
                        <p className={styles.certError}>Please select a certificate option</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Buttons */}
            <div className={styles.btnGroup}>
              <button
                className={`${styles.cartBtn} ${inCart ? styles.cartBtnAdded : ""} ${sizeError ? styles.cartBtnError : ""}`}
                onClick={handleAddToCart}
                disabled={inCart}
              >
                {inCart ? "✓ In Cart" : added ? "Added!" : "Add to Cart"}
              </button>
              <button className={styles.inquireBtn} onClick={handleInquire}>
                <img src="/whatsapp-logo.png" alt="WhatsApp" width={18} height={18} />
                Inquire via WhatsApp
              </button>
            </div>

            {/* Trust badges */}
            <div className={styles.badges}>
              <div className={styles.badge}>🏆 Certified</div>
              <div className={styles.badge}>💎 Lifetime Warranty</div>
              <div className={styles.badge}>🚚 Free Delivery</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}